/**
 * TagLegend — horizontal scrollable row of tag chips with color dots.
 * Shows unique tags extracted from the given events/reminders.
 */

import React from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { getTagColor } from '../../utils/tagColors';

import { useTheme } from '../../utils/theme';
import { SPACING, RADIUS } from '../../utils/design-tokens';

interface Props {
  tags: string[];
  tagColors?: Record<string, string> | null;
  /** 'dark' = white text on dark bg (calendar hero), 'light' = dark text on light bg (reminders) */
  variant?: 'dark' | 'light';
  onPressTag?: (tag: string) => void;
  disabledTags?: ReadonlySet<string>;
  activeTag?: string | null;
}

export default function TagLegend({ tags, tagColors, variant = 'dark', onPressTag, disabledTags, activeTag }: Props) {
  const { colors } = useTheme();
  if (tags.length === 0) return null;
  const isLight = variant === 'light';

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.container}>
      {tags.map((tag) => {
        const color = getTagColor(tag, tagColors);
        const isActive = activeTag === tag;
        const isDisabled = disabledTags ? disabledTags.has(tag) : !!activeTag && !isActive;
        const isDimmed = isDisabled;

        const textColor = isActive
          ? (isLight ? colors.text : '#FFFFFF')
          : isLight
            ? (isDimmed ? colors.textDim : colors.textSecondary)
            : (isDimmed ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.65)');
        const itemBg = isActive
          ? (isLight ? color + '16' : color + '24')
          : isLight
            ? (isDimmed ? colors.text + '05' : colors.text + '0A')
            : (isDimmed ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.07)');
        const itemBorder = isActive
          ? color + (isLight ? '40' : '66')
          : isLight
            ? (isDimmed ? colors.text + '0A' : colors.text + '14')
            : (isDimmed ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.15)');

        const Wrapper = onPressTag ? TouchableOpacity : View;
        const wrapperProps = onPressTag
          ? {
              onPress: () => onPressTag(tag),
              activeOpacity: 0.7,
              accessibilityRole: 'button' as const,
              accessibilityState: { selected: isActive },
            }
          : {};

        return (
          <Wrapper key={tag} {...wrapperProps} style={[styles.item, { backgroundColor: itemBg, borderColor: itemBorder, opacity: isDimmed ? 0.52 : 1 }]}>
            <View style={[styles.dot, { backgroundColor: color }]} />
            <Text style={[styles.text, { color: textColor }]} numberOfLines={1}>{tag}</Text>
          </Wrapper>
        );
      })}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 6,
    paddingTop: SPACING.xs,
    paddingBottom: 2,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexShrink: 0,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.xs,
    borderRadius: RADIUS.full,
    borderWidth: 0.5,
    gap: 6,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
  },
  text: {
    fontSize: 9.5,
    fontWeight: '500',
    letterSpacing: 0.3,
  },
});
